import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <section id="about" className="section bg-white">
      <div className="container">
        <div className="text-center mb-12">
          <motion.h2 
            className="text-primary-700 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Why Smooth Staffing?
          </motion.h2>
          <motion.p 
            className="text-neutral-600 text-xl max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <span className="font-semibold italic">We empower businesses to scale without overhead. Our 5-phase Business Overhaul™ ensures seamless VA integration, from CRM optimization to revenue growth.</span>
          </motion.p>
        </div>

        <div className="flex flex-col gap-8 mb-16">
          <motion.div
            className="rounded-2xl overflow-hidden shadow-normal bg-primary-50 p-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-primary-700 mb-6 text-center">Key Differentiators</h3>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-soft text-center"
                variants={itemVariants}
              >
                <div className="text-4xl text-accent-600 mb-4">🏆</div>
                <h4 className="font-bold text-primary-700 mb-2">5-Step System Audit</h4>
                <p className="text-neutral-600">Comprehensive business analysis to identify optimization opportunities</p>
              </motion.div>
              
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-soft text-center"
                variants={itemVariants}
              >
                <div className="text-4xl text-accent-600 mb-4">💼</div>
                <h4 className="font-bold text-primary-700 mb-2">Industry-Trained VAs</h4>
                <p className="text-neutral-600">Specialists with experience in your specific business sector</p>
              </motion.div>
              
              <motion.div 
                className="bg-white rounded-xl p-6 shadow-soft text-center"
                variants={itemVariants}
              >
                <div className="text-4xl text-accent-600 mb-4">🔒</div>
                <h4 className="font-bold text-primary-700 mb-2">Results Guaranteed</h4>
                <p className="text-neutral-600">If you don't like the results, you can cancel within a month</p>
              </motion.div>
            </motion.div>
          </motion.div>
          
          <motion.div
            className="rounded-2xl overflow-hidden shadow-normal"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="flex relative">
              {/* Left column - Smooth Staffing */}
              <div className="w-1/2 bg-primary-700 p-6 text-white">
                <h3 className="text-xl font-bold mb-4 text-center">Smooth Staffing</h3>
                <ul className="space-y-4">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>From $4/hour (no hidden fees)</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Pre-vetted, ready-to-work experts</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Easy, guided setup</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Better retention</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Full access to AHT resources</span>
                  </li>
                </ul>
              </div>
              
              {/* VS Circle in the middle */}
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full w-16 h-16 flex items-center justify-center shadow-normal z-10">
                <span className="text-primary-700 font-bold text-xl">VS</span>
              </div>
              
              {/* Right column - Hiring In-House */}
              <div className="w-1/2 bg-accent-600 bg-opacity-10 p-6 text-neutral-700">
                <h3 className="text-xl font-bold mb-4 text-center">Hiring In-House</h3>
                <ul className="space-y-4">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-primary-700 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>$60k+/year + benefits</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-primary-700 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Months of training</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-primary-700 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Stressful onboarding</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-primary-700 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>Rarely has good retention</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-primary-700 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>No access to AHT resources</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-xl p-6 shadow-normal border border-neutral-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center">
              <div className="text-accent-600 mr-5">
                <svg className="w-16 h-16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 17V7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 18C7 15.7909 9.23858 14 12 14C14.7614 14 17 15.7909 17 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <h4 className="text-xl font-bold text-primary-700 mb-1">20+ Hours Saved Weekly</h4>
                <p className="text-neutral-600">Reclaim your time and focus on growing your business</p>
              </div>
            </div>
          </motion.div>
        </div>
        
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <a 
              href="https://calendly.com/adam-cothes/va-consultation" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Book Your Free Consultation
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;